import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, Heart, MapPin, X } from 'lucide-react';
import LoginModal from '@/components/LoginModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Browse = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [bands, setBands] = useState([]);
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginMessage, setLoginMessage] = useState('');
  const [pendingBandId, setPendingBandId] = useState(null);

  useEffect(() => {
    const fetchGenres = async () => {
      try {
        const res = await fetch(`${API}/bands/genres`);
        if (res.ok) {
          const data = await res.json();
          setGenres(data);
        }
      } catch (error) {
        console.error('Error fetching genres:', error);
      }
    };
    fetchGenres();
  }, []);

  useEffect(() => {
    const fetchBands = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (searchQuery) params.append('search', searchQuery);
        if (selectedGenre) params.append('genre', selectedGenre);
        
        const res = await fetch(`${API}/bands?${params.toString()}`);
        if (res.ok) {
          const data = await res.json();
          setBands(data);
        }
      } catch (error) {
        console.error('Error fetching bands:', error);
      } finally {
        setLoading(false);
      }
    };
    
    const debounce = setTimeout(fetchBands, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, selectedGenre]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedGenre('');
  };

  const handleBandClick = (band) => {
    if (isAuthenticated) {
      navigate(`/band/${band.band_id}`);
    } else {
      setLoginMessage(`Sign in to view ${band.name}'s full profile, listen to their music, and save your favorites.`);
      setPendingBandId(band.band_id);
      setLoginModalOpen(true);
    }
  };

  const handleLoginSuccess = () => {
    // Navigate to the band page after successful login
    if (pendingBandId) {
      navigate(`/band/${pendingBandId}`);
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen py-8">
      {/* Login Modal */}
      <LoginModal 
        open={loginModalOpen} 
        onOpenChange={(open) => {
          setLoginModalOpen(open);
          if (!open) setPendingBandId(null);
        }}
        message={loginMessage}
        onSuccess={handleLoginSuccess}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <h1 className="font-accent text-5xl sm:text-6xl text-foreground mb-4">
            BROWSE BANDS
          </h1>
          <p className="font-mono text-sm text-muted-foreground uppercase tracking-widest">
            Discover your next favorite artist
          </p>
        </div>

        {/* Search & Filters */}
        <div className="mb-8 space-y-4">
          {/* Search Input */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search bands..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-input border-2 border-border focus:border-primary font-mono"
              data-testid="search-input"
            />
          </div>

          {/* Genre Filters */}
          <div className="flex flex-wrap gap-2 items-center">
            <span className="font-mono text-xs text-muted-foreground uppercase mr-2">Genres:</span>
            {genres.map((genre) => (
              <Button
                key={genre}
                variant={selectedGenre === genre ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedGenre(selectedGenre === genre ? '' : genre)}
                className={`font-mono text-xs uppercase ${
                  selectedGenre === genre 
                    ? 'bg-primary text-black border-primary' 
                    : 'border-border text-muted-foreground hover:border-primary hover:text-primary'
                }`}
                data-testid={`genre-filter-${genre}`}
              >
                {genre}
              </Button>
            ))}
            
            {(searchQuery || selectedGenre) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="font-mono text-xs text-destructive hover:text-destructive/80"
                data-testid="clear-filters-btn"
              >
                <X className="w-3 h-3 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6 border-b-2 border-border pb-4">
          <span className="font-mono text-sm text-muted-foreground">
            {loading ? 'Loading...' : `${bands.length} bands found`}
          </span>
        </div>

        {/* Bands Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-pulse text-primary font-accent text-2xl">LOADING...</div>
          </div>
        ) : bands.length === 0 ? (
          <div className="text-center py-20">
            <p className="font-mono text-muted-foreground mb-4">No bands found matching your criteria</p>
            <Button
              variant="outline"
              onClick={clearFilters}
              className="font-mono uppercase"
              data-testid="reset-search-btn"
            >
              Reset Search
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {bands.map((band) => (
              <div 
                key={band.band_id}
                onClick={() => handleBandClick(band)}
                className="group cursor-pointer"
                data-testid={`band-card-${band.band_id}`}
              >
                <div className="border-2 border-border bg-card overflow-hidden card-brutal h-full">
                  {/* Band Image */}
                  <div className="aspect-square overflow-hidden">
                    {band.image_urls?.[0] ? (
                      <img 
                        src={band.image_urls[0]}
                        alt={band.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-muted flex items-center justify-center">
                        <span className="font-accent text-5xl text-muted-foreground">
                          {band.name[0]}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Band Info */}
                  <div className="p-4 border-t-2 border-border">
                    <h3 className="font-sans text-lg font-bold text-foreground group-hover:text-primary transition-colors">
                      {band.name}
                    </h3>
                    
                    <div className="flex flex-wrap gap-1 mt-2">
                      {band.genre_tags?.slice(0, 3).map((tag, i) => (
                        <Badge 
                          key={i}
                          variant="outline"
                          className="font-mono text-[10px] uppercase px-1.5 py-0 border-muted-foreground/30 text-muted-foreground"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    
                    <p className="font-mono text-xs text-muted-foreground mt-3 line-clamp-2">
                      {band.bio}
                    </p>
                    
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
                      {band.location ? (
                        <span className="flex items-center gap-1 font-mono text-xs text-muted-foreground">
                          <MapPin className="w-3 h-3" />
                          {band.location}
                        </span>
                      ) : (
                        <span></span>
                      )}
                      <div className="flex items-center gap-1 text-secondary font-mono text-xs">
                        <Heart className="w-3 h-3" />
                        {band.heart_count}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Browse;
